<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <v-row>
          <v-col cols="12" class="submission-detail-section">
            <v-container class="patient-information-form-wrapper">
              <router-link class="detail-page__back" :to="listTarget">
                <v-icon size="18">mdi-arrow-left</v-icon>
                <span>{{ backLabel }}</span>
              </router-link>
              <v-row align="center">
                <v-col cols="12" md>
                  <h1 class="text-primary weight-600">Create New Submission</h1>
                </v-col>
                <v-col v-if="!pageLoadFailed" cols="12" md="auto" class="d-flex align-center">
                  <v-checkbox
                    v-model="autoSaveEnabled"
                    label="Auto-save draft"
                    color="primary"
                    density="compact"
                    hide-details />
                  <v-tooltip location="bottom" max-width="320">
                    <template #activator="{ props: tooltipProps }">
                      <v-btn
                        v-bind="tooltipProps"
                        icon="mdi-information-outline"
                        variant="text"
                        size="x-small"
                        aria-label="About auto-save"
                        class="text-medium-emphasis" />
                    </template>
                    Keeps a draft of this submission as you fill in the form, so a reload or a closed tab does not lose
                    your work. Starts once the patient's first and last name are entered. Reopen the draft later from
                    your list.
                  </v-tooltip>
                </v-col>
              </v-row>
              <SearchPatientByExternalIdView class="mb-4" @prepopulate-order-info="handleSearchPrepopulate" />
              <div v-if="preloader">
                <SubmissionPageSkeleton />
              </div>
              <!-- The page couldn't be set up at all (e.g. account settings failed to load) —
                   show only the error, never a form driven by settings we don't have. -->
              <div v-else-if="pageLoadFailed" class="mt-4">
                <v-alert type="error">
                  <div v-for="error in errors" :key="error">{{ error }}</div>
                </v-alert>
              </div>
              <div v-else>
                <!-- Error display -->
                <div v-if="errors.length > 0" class="mt-4">
                  <v-alert type="error">
                    <div v-for="error in errors" :key="error">{{ error }}</div>
                  </v-alert>
                </div>
                <v-row ref="formStartRef">
                  <v-col cols="12" class="submission-detail-section">
                    <AutofillFromDocumentsComponent
                      v-if="autofillEnabled"
                      :supporting-docs-count="supportingDocsCount"
                      :is-running="autofillInProgress"
                      :documents-busy="documentsBusy"
                      :status="autofillStatus"
                      :error="autofillError"
                      :candidate-count="autofillCandidateCount"
                      :candidates="autofillCandidates"
                      :diagnoses="autofillDiagnoses"
                      :medication-matches="autofillMedicationMatches"
                      @run="handleRunAutofill"
                      @start-another="handleStartAnotherSubmission">
                      <OrderDocumentList
                        ref="autofillDocumentListRef"
                        :patient-chart="orderInfo.patientChart"
                        :ai-reference-documents="orderInfo.aiReferenceDocuments || []"
                        :medical-necessity="orderInfo.medicalNecessity"
                        :is-editable="true"
                        :cmm-budgeted="documentsCmmBudgeted"
                        :order-key="orderInfo.orderKey"
                        :form-button-clicked="formButtonClicked"
                        @add-uploaded-file="updateSupportingDocs"
                        @delete-uploaded-file="handleDeleteFile"
                        @update-ai-reference-documents="updateAiReferenceDocuments" />
                    </AutofillFromDocumentsComponent>

                    <v-expansion-panels v-if="!preloader" v-model="expandedSections" multiple>
                      <!-- Patient Information Section -->
                      <v-expansion-panel value="patient-info">
                        <v-expansion-panel-title class="text-primary weight-600">
                          <v-icon icon="mdi-account" class="mr-2"></v-icon>
                          Patient Information
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                          <PatientInformationFormPreview
                            :order-info="orderInfo"
                            :is-editable="true"
                            :form-button-clicked="formButtonClicked"
                            :autofill-candidates="autofillCandidates"
                            @apply-autofill="handleApplyAutofillChip"
                            @update-order-info-field="updateOrderInfoField" />
                        </v-expansion-panel-text>
                      </v-expansion-panel>

                      <!-- Additional Patient Charts (multi-patient chart support) -->
                      <v-alert
                        v-if="
                          orderInfo.additionalPatientExternalIds && orderInfo.additionalPatientExternalIds.length > 0
                        "
                        type="info"
                        variant="tonal"
                        class="mb-4">
                        <div class="text-subtitle-2 mb-1">Additional Patient Charts</div>
                        <div class="d-flex flex-wrap ga-2">
                          <v-chip
                            v-for="patientChart in orderInfo.additionalPatientExternalIds"
                            :key="patientChart.externalId"
                            size="small"
                            variant="outlined">
                            {{ patientChart.firstName }} {{ patientChart.lastName }} — Patient ID (MRN):
                            {{ patientChart.externalId }}
                          </v-chip>
                        </div>
                      </v-alert>

                      <!-- Provider Information Section -->
                      <v-expansion-panel value="provider-info">
                        <v-expansion-panel-title class="text-primary weight-600">
                          <v-icon icon="mdi-doctor" class="mr-2"></v-icon>
                          Provider Information
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                          <ProviderInformationFormPreview
                            :order-info="orderInfo"
                            :is-editable="true"
                            :form-button-clicked="formButtonClicked"
                            :autofill-candidates="autofillCandidates"
                            @apply-autofill="handleApplyAutofillChip"
                            @update-order-info-field="updateOrderInfoField" />
                        </v-expansion-panel-text>
                      </v-expansion-panel>

                      <!-- Medical Information Section -->
                      <v-expansion-panel value="medical-info">
                        <v-expansion-panel-title class="text-primary weight-600">
                          <v-icon icon="mdi-medication" class="mr-2"></v-icon>
                          Prescription Information
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                          <MedicalInformationForm
                            :order-info="orderInfo"
                            :is-editable="true"
                            :form-button-clicked="formButtonClicked"
                            :autofill-candidates="autofillCandidates"
                            :allow-additional-medications="true"
                            :medication-matches="autofillMedicationMatches"
                            @apply-autofill="handleApplyAutofillChip"
                            @apply-medication="handleApplyAutofillMedication"
                            @update-order-info-field="updateOrderInfoField"
                            @add-extra-medication="addExtraMedication"
                            @remove-extra-medication="removeExtraMedication"
                            @remove-primary-medication="removePrimaryMedication"
                            @update-extra-medication="updateExtraMedicationField"
                            @add-additional-medication="addAdditionalMedication"
                            @remove-additional-medication="removeAdditionalMedication"
                            @update-additional-medication-field="updateAdditionalMedicationField" />
                        </v-expansion-panel-text>
                      </v-expansion-panel>

                      <!-- Patient Diagnosis Information Section -->
                      <v-expansion-panel value="diagnosis-info">
                        <v-expansion-panel-title class="text-primary weight-600">
                          <v-icon icon="mdi-account-heart" class="mr-2"></v-icon>
                          Patient Diagnosis Information
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                          <v-row class="patient-information-container">
                            <v-col cols="12">
                              <v-row class="patient-information">
                                <v-col cols="12" class="header-with-select-box">
                                  <DiagnosisSelectionPreviewComponent
                                    :medication="orderInfo.medication"
                                    :order-key="orderInfo.orderKey"
                                    :primary-diagnosis-name="orderInfo.primaryDiagnosisName"
                                    :primary-diagnosis-icd10-code="orderInfo.primaryDiagnosisIcd10Code"
                                    :secondary-diagnosis="orderInfo.secondaryDiagnosis"
                                    :is-editable="true"
                                    :form-button-clicked="formButtonClicked"
                                    @update-primary-diagnosis="updatePrimaryDiagnosis"
                                    @update-other-diagnosis="updateOtherDiagnosis"
                                    @add-other-diagnosis="addOtherDiagnosis" />
                                  <AutofillDiagnosisChoices
                                    v-if="autofillEnabled"
                                    :diagnoses="autofillDiagnoses"
                                    :order-info="orderInfo"
                                    @apply-diagnosis="handleApplyAutofillDiagnosis" />
                                </v-col>
                              </v-row>
                            </v-col>
                          </v-row>
                        </v-expansion-panel-text>
                      </v-expansion-panel>

                      <!-- Supporting Documents Section -->
                      <v-expansion-panel value="supporting-docs">
                        <v-expansion-panel-title class="text-primary weight-600">
                          <v-icon icon="mdi-file-document" class="mr-2"></v-icon>
                          {{ autofillEnabled ? "Statement of Medical Necessity" : "Documents" }}
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                          <SupportingDocumentsPreviewComponent
                            :order-key="orderInfo.orderKey"
                            :medical-necessity="orderInfo.medicalNecessity"
                            :medical-necessity-letter="orderInfo.medicalNecessityLetter"
                            :benefit-type="orderInfo.benefitType"
                            :patient-chart="orderInfo.patientChart"
                            :medical-necessity-template="orderInfo.medicalNecessityTemplate"
                            :ai-reference-documents="orderInfo.aiReferenceDocuments || []"
                            :form-button-clicked="formButtonClicked"
                            :is-editable="true"
                            :show-document-list="!autofillEnabled"
                            :cmm-budgeted="documentsCmmBudgeted"
                            @add-uploaded-file="updateSupportingDocs"
                            @delete-uploaded-file="handleDeleteFile"
                            @update-ai-reference-documents="updateAiReferenceDocuments" />
                        </v-expansion-panel-text>
                      </v-expansion-panel>

                      <!-- Submission Options Section: Smart Submit DOA attestation, expedited review, fax form (LAM-5197) -->
                      <v-expansion-panel value="submission-options">
                        <v-expansion-panel-title class="text-primary weight-600">
                          <v-icon icon="mdi-tune-variant" class="mr-2"></v-icon>
                          Submission Options
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                          <DoaAttestationComponent
                            v-if="
                              doaAttestationEnabled &&
                              orderInfo.submissionMethod === 'CoverMyMeds' &&
                              !orderInfo.faxSubmissionFormDetail
                            "
                            v-model="orderInfo.prescriberDoaAttested"
                            :is-editable="true" />
                          <ExpeditedReviewComponent
                            :order-key="orderInfo.orderKey"
                            :expedited-review-within24h="orderInfo.expeditedReviewWithin24h"
                            :expedited-review-statement="orderInfo.expeditedReviewStatement"
                            :is-editable="true"
                            :form-button-clicked="formButtonClicked"
                            @update-order-info-field="updateOrderInfoField" />
                          <!-- Only for clients with mapped fax forms -->
                          <FaxFormSelectionComponent
                            v-if="faxForms.length > 0"
                            :fax-forms="faxForms"
                            :fax-submission-form-detail="orderInfo.faxSubmissionFormDetail"
                            :is-editable="true"
                            @update:opted-in="faxFormOptedIn = $event"
                            @update-order-info-field="updateOrderInfoField" />
                          <v-alert v-if="faxFormsError" type="warning" variant="tonal" class="mt-4">
                            {{ faxFormsError }}
                          </v-alert>
                        </v-expansion-panel-text>
                      </v-expansion-panel>
                    </v-expansion-panels>

                    <!-- Validation status -->
                    <v-card class="mb-4 mt-4">
                      <v-card-title>Validation Status</v-card-title>
                      <v-card-text>
                        <v-chip :color="validPatientInfo() ? 'success' : 'error'" class="mr-2">
                          Patient Info: {{ validPatientInfo() ? "Valid" : "Invalid" }}
                        </v-chip>
                        <v-chip :color="validProviderInfo() ? 'success' : 'error'" class="mr-2">
                          Provider Info: {{ validProviderInfo() ? "Valid" : "Invalid" }}
                        </v-chip>
                        <v-chip :color="validMedicationInfo() ? 'success' : 'error'" class="mr-2">
                          Medication Info: {{ validMedicationInfo() ? "Valid" : "Invalid" }}
                        </v-chip>
                        <v-chip :color="validDiagnosisInfo() ? 'success' : 'error'" class="mr-2">
                          Diagnosis Info: {{ validDiagnosisInfo() ? "Valid" : "Invalid" }}
                        </v-chip>
                      </v-card-text>
                    </v-card>

                    <SubmissionRoutingBars
                      v-if="orderInfo.orderKey"
                      :order-info="orderInfo"
                      @update-order-info-field="updateOrderInfoField" />

                    <!-- Action buttons for new submission creation -->
                    <v-row class="mt-4">
                      <v-col v-if="!autoSaveEnabled" cols="auto">
                        <v-btn
                          variant="outlined"
                          :disabled="draftSavingInProgress"
                          :loading="draftSavingInProgress"
                          @click="handleSaveDraft()">
                          Save Draft
                        </v-btn>
                      </v-col>
                      <v-col cols="auto">
                        <v-btn :disabled="draftSavingInProgress" @click="handleCreateSubmission">
                          Create Submission
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </div>
            </v-container>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-row v-if="route.params?.id">
      <v-col cols="12">
        <br />
        <AuditLogList :order-info-id="route.params?.id" />
      </v-col>
    </v-row>
    <v-dialog v-model="showCreateSuccessDialog" max-width="500" persistent>
      <v-card>
        <v-card-title class="text-h6" color="green">
          {{ batchSubmissionResults ? "Submissions Created" : "Submission Created" }}
        </v-card-title>
        <v-card-text>
          <template v-if="batchSubmissionResults">
            <div>
              {{ batchSubmissionResults.filter((result) => result.success).length }} of
              {{ batchSubmissionResults.length }} medications submitted successfully. Fetching forms and questions from
              external portals may take up to 15 minutes.
            </div>
            <v-list density="compact" class="mt-2">
              <v-list-item v-for="(result, index) in batchSubmissionResults" :key="index">
                <template #prepend>
                  <v-icon
                    :color="result.success ? 'success' : 'error'"
                    :icon="result.success ? 'mdi-check-circle' : 'mdi-alert-circle'" />
                </template>
                <v-list-item-title>{{ result.medication }}</v-list-item-title>
                <v-list-item-subtitle v-if="!result.success">
                  {{ result.skippedDuplicate ? "Skipped — duplicate medication" : result.error }}
                </v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </template>
          <template v-else>
            Your submission has been created successfully. Fetching form and questions from external portal may take up
            to 15 minutes. You may return to your list or create another submission.
            <SubmissionRoutingBars
              v-if="orderInfo.orderKey"
              class="mt-4"
              :order-info="orderInfo"
              @update-order-info-field="updateOrderInfoField" />
          </template>
        </v-card-text>
        <v-card-actions class="pt-0">
          <v-spacer />
          <v-btn variant="outlined" @click="onSuccessReturnToAll">Return to List</v-btn>
          <v-btn variant="flat" color="primary" @click="onSuccessCreateAnother">Create Another Submission</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-snackbar v-model="showAutoSaveToast" location="top right" color="success" variant="tonal" timeout="5000">
      <v-icon icon="mdi-content-save-check-outline" size="18" class="mr-2" />
      {{ autoSaveMessage }}
    </v-snackbar>
  </v-container>
</template>

<script setup>
import { useMarkCaseOpened } from "@/composables/useCaseReadState";
import { computed, ref, watch, onBeforeMount, onUnmounted, nextTick, toRaw } from "vue";
import debounce from "lodash.debounce";
import { useRouter, useRoute } from "vue-router";
import { listReturnTarget } from "@/composables/useListReturn";
import { priorAuthWorklistRoute, worklistShownFor } from "@/constants/worklistSections";
import { storeToRefs } from "pinia";
import { useOrderInfoStore } from "@/stores/orderInfoStore";
import { useRequirementsStore } from "@/stores/requirementsStore";
import { useOrderInfoUpdates } from "@/composables/useOrderInfoUpdates";
import { tryParseApiErrors, clearOrderInfoStorage } from "@/utils";
import { convertStateToFullName } from "@/utils/usaStates";
import PatientInformationFormPreview from "@/pages/PatientInformationFormPreview.vue";
import ProviderInformationFormPreview from "@/pages/ProviderInformationFormPreview.vue";
import MedicalInformationForm from "@/pages/MedicalInformationForm.vue";
import DiagnosisSelectionPreviewComponent from "@/components/DiagnosisSelectionPreviewComponent.vue";
import SupportingDocumentsPreviewComponent from "@/components/SupportingDocumentsPreviewComponent.vue";
import OrderDocumentList from "@/components/OrderDocumentList.vue";
import { MAX_AI_ONLY_DOCUMENTS } from "@/composables/useChartUpload";
import { s3KeyOf } from "@/utils/s3Links";
import SubmissionPageSkeleton from "@/components/SubmissionPageSkeleton.vue";
import AuditLogList from "@/components/AuditLogList.vue";
import SearchPatientByExternalIdView from "@/components/SearchPatientByExternalIdView.vue";
import { missionControlService } from "@/services/missionControlService";
import AutofillFromDocumentsComponent from "@/components/AutofillFromDocumentsComponent.vue";
import AutofillDiagnosisChoices from "@/components/AutofillDiagnosisChoices.vue";
import { MedicationDetail } from "@/models/medicationDetail";
import SubmissionRoutingBars from "@/components/dashboard/SubmissionRoutingBars.vue";
import {
  AUTOFILL_DIAGNOSIS_SLOT,
  AUTOFILL_POLL_INTERVAL_MS,
  AUTOFILL_POLL_MAX_CONSECUTIVE_FAILURES,
  AUTOFILL_POLL_TIMEOUT_MS,
  AUTOFILL_STATUS,
} from "@/constants/autofill";
import ExpeditedReviewComponent from "@/components/ExpeditedReviewComponent.vue";
import FaxFormSelectionComponent from "@/components/FaxFormSelectionComponent.vue";
import DoaAttestationComponent from "@/components/DoaAttestationComponent.vue";
import { orderInfoService } from "@/services/orderInfoService";
import { mainServices } from "@/services/mainServices";

const { orderInfo } = storeToRefs(useOrderInfoStore());
const requirementsStore = useRequirementsStore();
const route = useRoute();
useMarkCaseOpened("orderKey", () => route.params.id);
const {
  formButtonClicked,
  updateOrderInfoField,
  addExtraMedication,
  removeExtraMedication,
  removePrimaryMedication,
  updateExtraMedicationField,
  addAdditionalMedication,
  removeAdditionalMedication,
  updateAdditionalMedicationField,
  addOtherDiagnosis,
  updatePrimaryDiagnosis,
  updateOtherDiagnosis,
  updateSupportingDocs,
  updateAiReferenceDocuments,
  isSessionUpload,
  validPatientInfo,
  validProviderInfo,
  validMedicationInfo,
  validAdditionalMedicationsInfo,
  validDiagnosisInfo,
  validSupportingDocs,
  savePartialOrderInfo,
} = useOrderInfoUpdates(orderInfo);

function handleDeleteFile(downloadLinkInfo) {
  // Mark as delete and use existing updateSupportingDocs handler
  downloadLinkInfo.isDelete = true;
  updateSupportingDocs(downloadLinkInfo);
}

const router = useRouter();
const expandedSections = ref([]);
const draftSavingInProgress = ref(false);
const errors = ref([]);
// Opening an existing draft starts in the skeleton: the client-config fetch awaited before loadOrder()
// otherwise paints the form empty for its whole round-trip (LAM-5432).
const preloader = ref(!!route.params.id);
// Set when the page can't be set up at all, so the form is replaced by the error rather than
// rendered alongside it.
const pageLoadFailed = ref(false);
const showCreateSuccessDialog = ref(false);
// Per-medication outcomes when additional medications were queued (LAM-4841) — null for an
// ordinary single-medication submission, which keeps showing today's plain success message.
const batchSubmissionResults = ref(null);
const formStartRef = ref(null);
// Per-tenant rollout flag, off by default (create_submission_autofill_enabled). The API refuses
// both kickoff and poll without it, so this only hides UI that could not work anyway.
const autofillEnabled = computed(
  () => requirementsStore.clientConfig?.priorAuth?.createSubmissionAutofillEnabled === true
);
// Smart Submit (LAM-5919): the DOA attestation can be given here and changed on the review page.
const doaAttestationEnabled = computed(
  () =>
    requirementsStore.clientConfig?.priorAuth?.delegateAccountSubmitRequired === true &&
    requirementsStore.clientConfig?.priorAuth?.smartSubmitEnabled === true
);
// Fax forms mapped to this company (LAM-5197) — empty keeps the whole fax panel unrendered.
const faxForms = ref([]);
const faxFormsSettled = ref(false);
// Its own banner instead of the shared `errors` list, which every load, save and Create resets —
// a one-shot fetch failure announced there is wiped before the user can read it.
const faxFormsError = ref("");
mainServices
  .getFaxForms()
  .then((forms) => {
    faxForms.value = Array.isArray(forms) ? forms : [];
  })
  .catch(() => {
    faxForms.value = [];
    faxFormsError.value =
      "Failed to load your mapped fax forms. Reload the page if this submission needs to go out over fax.";
  })
  .finally(() => {
    faxFormsSettled.value = true;
  });
const faxFormOptedIn = ref(false);
const autofillInProgress = ref(false);
const autofillStatus = ref(AUTOFILL_STATUS.IDLE);
const autofillError = ref("");
// Per-field candidate values from the last autofill run, keyed by orderInfo field name.
// The first candidate of each field is written to the form as soon as the run lands
// (applyAutofillDefaults); the rest stay clickable alternatives.
const autofillCandidates = ref(null);
// Diagnosis candidates are separate: they are (name, icd10Code) pairs, and each can go into
// the primary slot or be appended to the secondary list.
const autofillDiagnoses = ref([]);
// Catalog medications matched to the document's NDC or name. Shown as chips; applied as a whole row.
const autofillMedicationMatches = ref([]);
let autofillPollTimer = null;
// The autofill run whose prefilled values are on the form but not yet saved. The next save sends it
// so the server marks the run applied; from then on a reopen leaves blank fields blank.
const unsavedAutofillTaskId = ref(null);

const autofillDocumentListRef = ref(null);
const documentsBusy = computed(() => autofillDocumentListRef.value?.isUploading() === true);
const documentsCmmBudgeted = computed(
  () => orderInfo.value.submissionMethod === "CoverMyMeds" && !orderInfo.value.faxSubmissionFormDetail
);
const supportingDocsCount = computed(() => {
  const collectLinks = (docs) => (Array.isArray(docs) ? docs : []).flatMap((doc) => doc?.links || []);
  return [...collectLinks(orderInfo.value.patientChart), ...collectLinks(orderInfo.value.aiReferenceDocuments)].length;
});

const autofillCandidateCount = computed(
  () => Object.keys(autofillCandidates.value || {}).length + (autofillDiagnoses.value.length > 0 ? 1 : 0)
);

// Put the draft's key in the URL once it exists. Reopening a draft by id is what drives
// loadOrder() -> restoreAutofillRun(), so without this a reload of the page the draft was created
// on takes the no-id path and loses the run (and the uploads behind it) that is still on the order.
async function syncDraftKeyToUrl() {
  const orderKey = orderInfo.value.orderKey;
  if (!orderKey || route.params.id === orderKey) return;
  // Same route and component, so this only rewrites the URL — it does not re-run the page setup.
  // The path does change though, so the router's scrollBehavior jumps to the top. The router
  // scrolls in a microtask after the navigation resolves; the next animation frame runs after
  // that and before paint, so restoring there keeps the user on the field they are on.
  const { scrollX, scrollY } = window;
  await router.replace({ name: "create-submission", params: { id: orderKey } });
  await new Promise(requestAnimationFrame);
  window.scrollTo(scrollX, scrollY);
}

async function handleRunAutofill() {
  if (documentsBusy.value) return;
  autofillError.value = "";
  autofillCandidates.value = null;
  autofillDiagnoses.value = [];
  autofillMedicationMatches.value = [];
  autofillStatus.value = AUTOFILL_STATUS.QUEUED;
  autofillInProgress.value = true;
  try {
    // The job runs server-side against an order, but until now the uploads and form state live
    // only in browser memory — so mint the draft order first. This is why clicking autofill
    // creates a draft submission.
    await savePartialOrderInfo();
    syncDraftKeyToUrl();
    const { taskId } = await orderInfoService.startCreateSubmissionAutofill(orderInfo.value.orderKey, {});
    pollForAutofill(taskId, Date.now());
  } catch (error) {
    autofillInProgress.value = false;
    autofillStatus.value = AUTOFILL_STATUS.IDLE;
    autofillError.value = tryParseApiErrors(error).join(" ") || "Failed to start autofill. Please try again.";
  }
}

/** Opens a blank Create Submission in a new tab; no noopener, so the tab inherits this one's active company. */
function handleStartAnotherSubmission() {
  window.open(router.resolve({ name: "create-submission" }).href, "_blank");
}

// One extracted value goes into one form field — from a chip click under the field (AutofillFieldChoices),
// or from applyAutofillDefaults.
// The state selects only offer full names, so an extracted "CA"/"ca" is converted to "California";
// a value matching no state is not applied at all — its chip stays visible but never turns green.
function handleApplyAutofillValue({ field, value }) {
  if (field === "patientState" || field === "providerState") {
    value = convertStateToFullName(value);
    if (!value) return;
  }
  updateOrderInfoField(field, value);
}

// A catalog medication is applied as a whole row (name, NDC, package unit, client defaults).
// A chip click overwrites; the run's auto-apply keeps anything the user already typed.
function handleApplyAutofillMedication(match, blanksOnly = false) {
  match.applyTo(
    orderInfo.value,
    requirementsStore.clientConfig?.priorAuth?.medicationDefaults,
    updateOrderInfoField,
    blanksOnly
  );
}

// Field candidates, diagnoses and catalog medication rows from one finished run.
function setAutofillResult(result) {
  autofillCandidates.value = result?.fields || {};
  autofillDiagnoses.value = result?.diagnoses || [];
  autofillMedicationMatches.value = (result?.medicationMatches || []).map((row) => new MedicationDetail(row));
}

// A chip click under a field. Swapping the medication also drops the NDC and package size unit:
// those belong to the previous drug (a search pick sets all three together, so a chip pick must
// not leave the old pair behind to go to CMM with the new name).
function handleApplyAutofillChip(payload) {
  const medicationChanged = payload.field === "medication" && payload.value !== orderInfo.value.medication;
  handleApplyAutofillValue(payload);
  if (medicationChanged) {
    updateOrderInfoField("nationalDrugCode", "");
    updateOrderInfoField("packageSizeUnit", "");
  }
}

// Only a field with a visible control (and a chip under it) takes a default: a value written
// to a field the form does not render would be saved without the user ever seeing it.
// providerOfficeName has no input on the form; packageSizeUnit only renders for CoverMyMeds.
function isRenderedAutofillField(field) {
  if (field === "providerOfficeName") return false;
  if (field === "packageSizeUnit") return orderInfo.value.submissionMethod === "CoverMyMeds";
  return true;
}

function isOrderInfoFieldFilled(field) {
  const value = orderInfo.value[field];
  return value !== null && value !== undefined && value !== "";
}

// Fill the form with the first candidate of every field the run found, so the user reviews filled
// values instead of clicking each one in. Only blank fields are written: anything already on the
// order was typed or picked deliberately and must not be overwritten. The green "applied" chip under
// each field is derived from the current form value, so it lights up on its own — and the other
// candidates stay clickable where the documents offered more than one.
function applyAutofillDefaults(taskId) {
  unsavedAutofillTaskId.value = taskId;
  // The medication goes first: a catalog row carries its own NDC and package unit, and the loop
  // below then sees those fields as filled rather than overwriting them with free-text candidates.
  applyAutofillMedicationDefault();
  Object.entries(autofillCandidates.value || {}).forEach(([field, candidates]) => {
    const first = Array.isArray(candidates) ? candidates[0] : null;
    if (field === "medication" || !first) return;
    if (!isRenderedAutofillField(field) || isOrderInfoFieldFilled(field)) return;
    handleApplyAutofillValue({ field, value: first.value });
  });
  // Only the primary slot is filled: the backend sorts document-flagged primaries first, while the
  // remaining diagnoses are distinct conditions rather than alternatives for the same slot.
  const firstDiagnosis = autofillDiagnoses.value[0];
  if (firstDiagnosis && !orderInfo.value.primaryDiagnosisName) {
    handleApplyAutofillDiagnosis({ slot: AUTOFILL_DIAGNOSIS_SLOT.PRIMARY, diagnosis: firstDiagnosis });
  }
}

// Driven by the catalog matches, not the free-text medication candidate: an extraction that read
// only the NDC has a match and no candidate. One row is applied whole. Several mean the document
// did not name the drug exactly (one strength among many), so the first row is not guessed: the
// field takes the free-text name the documents gave and the rows stay as chips for the user to pick.
function applyAutofillMedicationDefault() {
  if (isOrderInfoFieldFilled("medication")) return;
  const matches = autofillMedicationMatches.value;
  if (matches.length === 1) {
    handleApplyAutofillMedication(matches[0], true);
    return;
  }
  const first = autofillCandidates.value?.medication?.[0];
  if (first) handleApplyAutofillValue({ field: "medication", value: first.value });
}

// A diagnosis is applied as a whole pair, into whichever slot the user chose.
function handleApplyAutofillDiagnosis({ slot, diagnosis }) {
  const pair = { name: diagnosis.name, icd10Code: diagnosis.icd10Code || null };
  if (slot === AUTOFILL_DIAGNOSIS_SLOT.PRIMARY) {
    updatePrimaryDiagnosis(pair);
    return;
  }
  // Written into the first blank slot directly rather than through updateOtherDiagnosis: that
  // helper reads a missing code as "clear this row" and would splice out an already-applied
  // secondary, and the model deliberately returns diagnoses it found with no ICD-10 code.
  if (!Array.isArray(orderInfo.value.secondaryDiagnosis)) {
    orderInfo.value.secondaryDiagnosis = [];
  }
  let slotIndex = orderInfo.value.secondaryDiagnosis.findIndex((entry) => !entry?.name);
  if (slotIndex === -1) {
    addOtherDiagnosis();
    slotIndex = orderInfo.value.secondaryDiagnosis.length - 1;
  }
  orderInfo.value.secondaryDiagnosis[slotIndex] = { ...pair };
}

// Restore the order's last autofill run. The uploaded files and extracted candidates live on the
// task, not in browser state, so a reload (or reopening a draft by id) would otherwise show an
// empty uploader and lose the results while the S3 objects sat orphaned.
// Drafts from before LAM-6050 kept autofill-only uploads on the run, not the order: carry them over as AI-only
// files while the run is still unapplied, so the save that marks it applied stores them and they are carried once.
function carryOverAutofillOnlyUploads(extraDocuments) {
  if (!extraDocuments?.length || (orderInfo.value.aiReferenceDocuments || []).length) return;
  const onOrder = new Set((orderInfo.value.patientChart || []).flatMap((doc) => doc?.links || []).map(s3KeyOf));
  const carried = extraDocuments
    .filter((doc) => doc.url && !onOrder.has(s3KeyOf(doc.url)))
    .slice(0, MAX_AI_ONLY_DOCUMENTS)
    .map((doc) => ({ label: doc.fileName || "Document", links: [doc.url], context: "" }));
  if (carried.length) updateOrderInfoField("aiReferenceDocuments", carried);
}

async function restoreAutofillRun() {
  if (!autofillEnabled.value || !orderInfo.value.orderKey) return;
  try {
    const result = await orderInfoService.getCreateSubmissionAutofill(orderInfo.value.orderKey);
    if (result.status === AUTOFILL_STATUS.NONE) return;
    if (result.status === AUTOFILL_STATUS.DONE) {
      setAutofillResult(result.result);
      autofillStatus.value = AUTOFILL_STATUS.DONE;
      // Values live on the task, not the order. Until a save has carried them onto the order the
      // server asks for the blanks to be filled; after that a blank field is one the user cleared
      // on purpose and must stay blank (LAM-6154). The chips stay clickable either way.
      if (result.applyDefaults) {
        carryOverAutofillOnlyUploads(result.extraDocuments);
        applyAutofillDefaults(result.taskId);
      }
      return;
    }
    if (result.status === AUTOFILL_STATUS.ERROR) {
      autofillError.value = result.error || "The last autofill run failed.";
      return;
    }
    // Still queued or processing — pick the poll back up on the same task.
    autofillStatus.value = result.status;
    autofillInProgress.value = true;
    pollForAutofill(result.taskId, Date.now());
  } catch (_error) {
    // Best-effort: failing to restore a previous run must never block creating a submission
    // by hand, so this stays silent rather than surfacing an error the user can't act on.
  }
}

function clearAutofillPoll() {
  if (autofillPollTimer) {
    clearTimeout(autofillPollTimer);
    autofillPollTimer = null;
  }
}

function pollForAutofill(taskId, startedAt, consecutiveFailures = 0) {
  clearAutofillPoll();
  autofillPollTimer = setTimeout(async () => {
    try {
      const result = await orderInfoService.getCreateSubmissionAutofill(orderInfo.value.orderKey, taskId);
      if (result.status === AUTOFILL_STATUS.DONE) {
        // Field names arrive camelized by the axios interceptor, so they already match
        // orderInfo's keys and can be applied with updateOrderInfoField directly.
        setAutofillResult(result.result);
        autofillStatus.value = AUTOFILL_STATUS.DONE;
        autofillInProgress.value = false;
        applyAutofillDefaults(taskId);
        if (autofillCandidateCount.value === 0) {
          autofillError.value = "No values could be read from the uploaded documents.";
        }
        return;
      }
      if (result.status === AUTOFILL_STATUS.ERROR) {
        autofillError.value = result.error || "Autofill failed. Please fill the form in manually.";
        autofillStatus.value = AUTOFILL_STATUS.IDLE;
        autofillInProgress.value = false;
        return;
      }
      autofillStatus.value = result.status;
      if (Date.now() - startedAt > AUTOFILL_POLL_TIMEOUT_MS) {
        autofillError.value = "Autofill timed out. Please try again or fill the form in manually.";
        autofillStatus.value = AUTOFILL_STATUS.IDLE;
        autofillInProgress.value = false;
        return;
      }
      pollForAutofill(taskId, startedAt);
    } catch (error) {
      // The run itself is server-side and unaffected by a failed status check, so keep polling
      // through a blip rather than throwing away an extraction that is still going to finish.
      // Both bounds still apply: the failure budget, and the overall timeout.
      const failures = consecutiveFailures + 1;
      if (failures < AUTOFILL_POLL_MAX_CONSECUTIVE_FAILURES && Date.now() - startedAt <= AUTOFILL_POLL_TIMEOUT_MS) {
        // eslint-disable-next-line no-console -- a swallowed retry needs some trace to debug from
        console.warn(`Autofill status check failed (${failures}), retrying`, error);
        pollForAutofill(taskId, startedAt, failures);
        return;
      }
      autofillError.value = tryParseApiErrors(error).join(" ") || "Failed to check autofill progress.";
      autofillStatus.value = AUTOFILL_STATUS.IDLE;
      autofillInProgress.value = false;
    }
  }, AUTOFILL_POLL_INTERVAL_MS);
}

onUnmounted(clearAutofillPoll);

// Which patient's source last prepopulated this form. Distinguishes a patient switch (must clear
// the user-entered medication queues — leaving them would file the previous patient's drugs under
// the new one) from a same-patient re-prepopulate (must keep them — no prepopulate source ever
// supplies extraMedications/draftAdditionalMedications, so a wipe here is unrecoverable).
const lastPrepopulatedPatientId = ref(null);

// A reopened draft has a patient on the form but no source id in this page session (the draft
// does not persist which patient row it was filled from), so the first prepopulate after a load
// cannot be judged by id. Every prepopulate source sends the patient's name and DOB, so that is
// the identity compared instead: re-picking the draft's own patient keeps the queues, picking a
// different one clears them.
function patientIdentity(fields) {
  return ["patientFirstName", "patientLastName", "patientDob"]
    .map((field) =>
      String(fields[field] ?? "")
        .trim()
        .toLowerCase()
    )
    .join("|");
}

async function prefillFromMissionControl() {
  const patientId = Number(route.query.patient);
  if (route.query.from !== "mission-control" || !patientId) return;
  try {
    const { patient, coverages } = await missionControlService.patient(patientId);
    const pharmacy = coverages.find((coverage) => coverage.isActive && coverage.benefitType === "pharmacy_benefits");
    const data = {
      patientFirstName: patient.firstName,
      patientLastName: patient.lastName,
      patientDob: patient.dob,
      patientExternalId: patient.mrn,
      patientPhone: patient.phoneNumber,
      patientMemberId: pharmacy?.memberId,
      insuranceProvider: pharmacy?.insuranceProvider,
      bin: pharmacy?.rxBin,
      processorControlNumber: pharmacy?.rxPcn,
      grpNumber: pharmacy?.groupNumber,
    };
    const filled = Object.fromEntries(Object.entries(data).filter(([, value]) => value));
    handlePrepopulateOrderInfo(filled, patientId);
    applyPriorAuthDefaults(Object.keys(filled));
    lastSavedSnapshot.value = orderInfoSnapshot(orderInfo.value);
  } catch {
    // Prefill is a convenience; the form still opens empty when the patient cannot be read.
  }
}

function handlePrepopulateOrderInfo(prepopulatedData, sourcePatientId) {
  const samePatient =
    lastPrepopulatedPatientId.value != null
      ? sourcePatientId != null && sourcePatientId === lastPrepopulatedPatientId.value
      : patientIdentity(prepopulatedData) === patientIdentity(orderInfo.value);
  lastPrepopulatedPatientId.value = sourcePatientId ?? null;
  // List of all fields that can be prepopulated from intake data
  // Clear these fields first to prevent old values from persisting when selection changes
  const prepopulatableFields = [
    // Patient fields
    "patientFirstName",
    "patientLastName",
    "patientDob",
    "patientExternalId",
    // Insurance fields
    "patientMemberId",
    "insuranceProvider",
    "insurancePlanType",
    "benefitType",
    "bin",
    "processorControlNumber",
    "grpNumber",
    // Medication fields
    "medication",
    "dailyDosage",
    "nationalDrugCode",
    "prescriptionNumber",
    "frequency",
    "expectedLengthOfTherapy",
    "quantityPer30Days",
    "daw",
    "routeAdministration",
    // Both medication queues belong to the PATIENT being switched away from: extraMedications are
    // extra drugs on this one submission's fax form, draftAdditionalMedications each become their own
    // PA at submit. Left populated, switching patient carries the previous patient's drugs onto the
    // new one — and draftAdditionalMedications would batch-submit them under that new patient.
    // initializeOrderInfo() already empties both on a fresh visit; a patient switch is the same
    // reset. But cleared ONLY on an actual patient switch (see patientSwitchOnlyFields below):
    // no prepopulate source supplies either queue, so on a same-patient re-prepopulate a clear
    // would silently destroy user-entered drugs with nothing to restore them.
    "extraMedications",
    "draftAdditionalMedications",
    // Provider fields
    "providerFirstName",
    "providerLastName",
    "providerTitle",
    "providerNpiNumber",
    "providerContactFax",
    "providerContactPhone",
    "providerAddress1",
    "providerCity",
    "providerState",
    "providerZip",
    "providerOfficeName",
    "providerSpecialty",
    // Patient demographics from order form
    "patientGender",
    "patientAddress1",
    "patientCity",
    "patientState",
    "patientZip",
    "patientWeight",
    "patientHeight",
    "patientDrugAllergies",
    "stageOfTherapy",
    // Diagnosis fields
    "primaryDiagnosisIcd10Code",
    "primaryDiagnosisName",
    // secondaryDiagnosis intentionally NOT cleared here — DiagnosisSelectionPreviewComponent
    // renders it directly as an array; clearing to null (this loop's generic default) would
    // break that render. Matches existing precedent — this field was never in the clear list.
    // Treatment
    "treatmentStartDate",
    // patientChart - replace, don't merge when new selection is made
    "patientChart",
    "aiReferenceDocuments",
    // Additional patient chart MRNs (multi-patient chart support)
    "additionalPatientExternalIds",
    // Fax-form structured data (Brooks import) — no dedicated form input, shown read-only
    // before prepopulation, but must still clear when a different source is selected.
    "extraStructuredInformation",
    // Fax destination fields resolved from the matched FaxSubmissionFormDetail (LAM-4720) — only
    // present in prepopulatedData when the newly selected row resolves to FAX; must still clear
    // here so a stale fax number/description from a PRIOR FAX-routed selection doesn't survive
    // switching to a row that resolves to CoverMyMeds instead.
    "sendFaxFormTo",
    "faxDescription",
    // Explicit fax-form selection (LAM-5197) — company-level, but a selection made for the prior
    // source must not silently ride into the newly prepopulated one.
    "faxSubmissionFormDetail",
  ];

  // Array-valued fields reset to [], never null — every consumer reads .length or v-for's them,
  // so the generic null default below would break the render.
  const arrayValuedFields = [
    "patientChart",
    "aiReferenceDocuments",
    "additionalPatientExternalIds",
    "extraMedications",
    "draftAdditionalMedications",
  ];

  // User-entered medication queues and AI-only files: cleared only on an actual patient switch.
  // Everything else in the clear list is either restored from the new payload or belongs to the
  // outgoing source.
  const patientSwitchOnlyFields = ["extraMedications", "draftAdditionalMedications", "aiReferenceDocuments"];

  // Cleared to "" rather than null. On an already-persisted draft (autofill and Save Draft both
  // mint the order) a null never clears server-side: save-order's _set_fields skips None, so the
  // previous patient's value would silently survive on the row while the form shows blank. ""
  // passes the backend's `is not None` check and actually clears it. Only these two need it —
  // no prepopulate source reliably supplies them, so "" is what gets persisted at submit.
  const stringClearedFields = ["patientExternalId", "prescriptionNumber"];

  // Uploads made on this page survive a same-patient prepopulate (the source's chart is added to them); a patient
  // switch drops them and the AI-only files, which the next save removes from the order.
  const keptUploads = samePatient ? (orderInfo.value.patientChart || []).filter(isSessionUpload).map(toRaw) : [];
  const droppedReferenceLinks = samePatient
    ? []
    : (orderInfo.value.aiReferenceDocuments || []).flatMap((doc) => doc?.links || []);

  // Clear all prepopulatable fields first to ensure old values don't persist
  prepopulatableFields.forEach((field) => {
    if (samePatient && patientSwitchOnlyFields.includes(field)) {
      return;
    }
    if (arrayValuedFields.includes(field)) {
      updateOrderInfoField(field, []);
    } else if (stringClearedFields.includes(field)) {
      updateOrderInfoField(field, "");
    } else {
      updateOrderInfoField(field, null);
    }
  });

  // Now apply the new prepopulated data
  Object.keys(prepopulatedData).forEach((field) => {
    if (prepopulatedData[field] !== null && prepopulatedData[field] !== undefined) {
      // Special handling for patientChart - replace with new data (already cleared above)
      if (field === "patientChart" && Array.isArray(prepopulatedData[field])) {
        updateOrderInfoField(field, prepopulatedData[field]);
      } else {
        updateOrderInfoField(field, prepopulatedData[field]);
      }
    }
  });
  if (keptUploads.length)
    updateOrderInfoField("patientChart", [...(orderInfo.value.patientChart || []), ...keptUploads]);
  if (droppedReferenceLinks.length) updateAiReferenceDocuments([], droppedReferenceLinks);
}

// SearchPatientByExternalIdView (search + source selection) renders above this entire form —
// after prepopulating, the viewport doesn't move on its own, so the user is left looking at
// whatever was directly under the button they just clicked (e.g. the imported-order card's
// read-only info) instead of the form fields they now need to review. Bring the form into view
// explicitly, same pattern already used for scroll-to-invalid-field below.
function handleSearchPrepopulate(prepopulatedData, sourcePatientId) {
  handlePrepopulateOrderInfo(prepopulatedData, sourcePatientId);
  nextTick(() => {
    formStartRef.value?.$el?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  });
}

onBeforeMount(async () => {
  clearOrderInfoStorage();
  // Needed on both paths now: the autofill card is gated on a per-tenant config flag, so the
  // config has to be loaded even when opening an existing draft by id.
  if (!requirementsStore.clientConfig) {
    try {
      await requirementsStore.refreshCurrentUserClientConfig();
    } catch (err) {
      // Which controls this page shows is config-driven, and the store nulls the cached config
      // before it fetches — so rendering the form anyway would show the wrong page. Fail the
      // whole page instead of loading a half-configured form.
      pageLoadFailed.value = true;
      errors.value = ["Failed to load your account settings. Please reload the page and try again."];
      // The pageLoadFailed alert sits behind the skeleton (v-else-if), so drop the preloader.
      preloader.value = false;
      return;
    }
  }
  if (route.params.id) {
    loadOrder(route.params.id);
  } else {
    initializeOrderInfo();
    applyPriorAuthDefaults();
    // Baseline for the auto-save watcher: an untouched new form must never mint a draft.
    lastSavedSnapshot.value = orderInfoSnapshot(orderInfo.value);
    prefillFromMissionControl();
  }

  formButtonClicked.value = !!route.params.id;
  expandedSections.value = [
    "patient-info",
    "provider-info",
    "medical-info",
    "diagnosis-info",
    "supporting-docs",
    "submission-options",
  ];
});

function initializeOrderInfo() {
  orderInfo.value = {
    ...orderInfo.value,
    formStep: "Review",
    submissionMethod: "CoverMyMeds",
    // A leftover orderSubmission from a previously viewed CMM-imported order flips isCmmImport,
    // hiding the insurance fields and exempting them from validation — never inherit one.
    orderSubmission: null,
    extraMedications: [],
    // Without this, additional medications queued (but not submitted, or just submitted) in a
    // prior session would silently carry into the next "Create Another Submission"/fresh visit
    // and get re-submitted for whatever patient the user enters next.
    draftAdditionalMedications: [],
    // Left unset, not emptied: a fresh draft must not inherit the last viewed order's routing, and an
    // empty statusTags would be saved over any tag the server adds to the draft meanwhile.
    statusTags: undefined,
    tagDueDates: undefined,
    assignees: undefined,
    assignedGroups: undefined,
    secondaryDiagnosis: [{ icd10Code: null, name: null }],
    patientChart: [],
    aiReferenceDocuments: [],
    removedAiReferenceLinks: [],
  };
}

function applyPriorAuthDefaults(keep = []) {
  const defaults = requirementsStore.clientConfig?.priorAuth?.defaults;
  if (!defaults || typeof defaults !== "object") return;
  Object.entries(defaults).forEach(([key, value]) => {
    if (value === null || value === undefined || keep.includes(key)) return;
    updateOrderInfoField(key, value);
  });
}

async function loadOrder(orderKey) {
  preloader.value = true;
  try {
    errors.value = [];
    const fetchedOrder = await orderInfoService.getOrderDetails(orderKey);
    orderInfo.value = fetchedOrder.orderInfo;
    orderInfo.value.formStep = "Review";
    // The queue is persisted on the draft (LAM-5966). The column is nullable and every consumer
    // reads .length or v-for's the list, so a null never reaches the page.
    orderInfo.value.draftAdditionalMedications = fetchedOrder.orderInfo.draftAdditionalMedications || [];
    // The fax-form pick is persisted on the draft in its own column (LAM-6052); this page holds it
    // under the key it saves on, and the watch below drops one this company no longer offers.
    orderInfo.value.faxSubmissionFormDetail = fetchedOrder.orderInfo.draftFaxFormName || null;
    // Preserve the real persisted submission method (e.g. a fax order created from imported
    // data) — only fall back to the default when the fetched order genuinely has none yet.
    // (Previously this unconditionally overwrote it with "CoverMyMeds", clobbering fax orders.)
    orderInfo.value.submissionMethod = fetchedOrder.orderInfo.submissionMethod || "CoverMyMeds";
    await restoreAutofillRun();
  } catch (err) {
    errors.value = ["Failed to load the order. Please try again later or start a new submission."];
    initializeOrderInfo();
  }
  lastSavedSnapshot.value = orderInfoSnapshot(orderInfo.value);
  preloader.value = false;
  // Restore filled blanks while the preloader hid the change from the watcher; save them once.
  if (unsavedAutofillTaskId.value) scheduleAutoSave();
}

// Remove selection if fax form becomes stale
watch([faxFormsSettled, faxForms, () => orderInfo.value.faxSubmissionFormDetail], ([settled, forms, pick]) => {
  if (!settled || !pick) return;
  if (!forms.some((form) => form.faxDestination === pick)) {
    orderInfo.value.faxSubmissionFormDetail = null;
  }
});

async function handleSaveDraft() {
  draftSavingInProgress.value = true;
  let saved = false;
  try {
    errors.value = [];
    // The baseline is the form as this save found it plus what the save itself wrote onto it, not
    // the form once the save returns: an edit made while the PUT is in flight must still read as
    // unsaved, or the queued autosave compares equal and skips it.
    const sentState = JSON.parse(JSON.stringify(orderInfo.value));
    const autofillAppliedTaskId = unsavedAutofillTaskId.value;
    const written = await savePartialOrderInfo({ autofillAppliedTaskId });
    syncDraftKeyToUrl();
    lastSavedSnapshot.value = orderInfoSnapshot({ ...sentState, ...written });
    // A run applied while this save was in flight stays pending for the next save.
    if (unsavedAutofillTaskId.value === autofillAppliedTaskId) unsavedAutofillTaskId.value = null;
    saved = true;
  } catch (error) {
    errors.value = ["Failed to save draft. Please try again."];
  }
  draftSavingInProgress.value = false;
  return saved;
}

// Auto-save (LAM-5674). Child forms emit field updates on @change, so orderInfo only mutates when
// a field loses focus or a selection is made — the deep watch below is therefore blur-granular,
// not keystroke-granular. The preference persists per browser; the snapshot compare skips saves
// whose only trigger was the previous save's own response being written back into orderInfo.
const AUTO_SAVE_STORAGE_KEY = "createSubmissionAutoSave";
const AUTO_SAVE_DEBOUNCE_MS = 3000;
const autoSaveEnabled = ref(localStorage.getItem(AUTO_SAVE_STORAGE_KEY) !== "false");
watch(autoSaveEnabled, (enabled) => {
  localStorage.setItem(AUTO_SAVE_STORAGE_KEY, String(enabled));
  // A save queued before the switch was turned off must not still run.
  if (!enabled) scheduleAutoSave.cancel();
});
const showAutoSaveToast = ref(false);
const autoSaveMessage = ref("");
const lastSavedSnapshot = ref(null);

// Child forms initialize their fields to null on mount and clear them to "" — neither is a change
// worth a save, so empty values are dropped before comparing.
function orderInfoSnapshot(value) {
  return JSON.stringify(value, (key, item) => (item === null || item === "" ? undefined : item));
}

// Minimum for a draft worth keeping: without a patient name the Submissions list row is unidentifiable.
function hasAutoSaveMinimum(value) {
  return Boolean(value.patientFirstName?.trim() && value.patientLastName?.trim());
}

async function runAutoSave() {
  if (!autoSaveEnabled.value) return;
  if (draftSavingInProgress.value) {
    scheduleAutoSave();
    return;
  }
  // Submit mutates orderInfo (merged docs, a new orderKey) before the dialog opens, so the watch can
  // queue a save that lands after the order is submitted — and that save would echo the store's
  // empty editableSubmissionForm back over the fax form the submit just created.
  if (showCreateSuccessDialog.value) return;
  if (!hasAutoSaveMinimum(orderInfo.value)) return;
  // Prefilled values not yet saved count as a change even when the snapshot matches.
  if (!unsavedAutofillTaskId.value && orderInfoSnapshot(orderInfo.value) === lastSavedSnapshot.value) return;
  const isNewDraft = !orderInfo.value.orderKey;
  if (!(await handleSaveDraft())) return;
  autoSaveMessage.value = isNewDraft ? "Draft submission created" : "Draft saved";
  showAutoSaveToast.value = true;
}
const scheduleAutoSave = debounce(runAutoSave, AUTO_SAVE_DEBOUNCE_MS);

watch(
  orderInfo,
  (value) => {
    // No baseline yet means page setup is still running (children emit defaults while the client
    // config loads) — nothing the user did.
    if (lastSavedSnapshot.value === null) return;
    if (!autoSaveEnabled.value || preloader.value || showCreateSuccessDialog.value) return;
    if (!hasAutoSaveMinimum(value)) return;
    if (orderInfoSnapshot(value) === lastSavedSnapshot.value) return;
    scheduleAutoSave();
  },
  { deep: true }
);
onUnmounted(() => scheduleAutoSave.cancel());

function normalizeMedicationName(name) {
  return (name || "").trim().toLowerCase();
}

async function submitAdditionalMedications() {
  const sharedFields = { ...orderInfo.value };
  delete sharedFields.draftAdditionalMedications;
  delete sharedFields.orderKey;
  // Routing set on this page belongs to the primary submission; the server would copy only the tags.
  delete sharedFields.statusTags;
  delete sharedFields.tagDueDates;
  delete sharedFields.assignees;
  delete sharedFields.assignedGroups;

  // Duplicate-of-primary check happens client-side because the primary medication is never
  // part of the batch request (it's already its own submission via createEmptyOrder/saveOrder
  // above) — the backend's own dedupe only sees draftAdditionalMedications against each other.
  const primaryKey = normalizeMedicationName(orderInfo.value.medication);
  const duplicatesOfPrimary = [];
  const toSubmit = [];
  orderInfo.value.draftAdditionalMedications.forEach((medication) => {
    if (normalizeMedicationName(medication.medication) === primaryKey) {
      duplicatesOfPrimary.push({ medication: medication.medication, success: false, skippedDuplicate: true });
    } else {
      toSubmit.push(medication);
    }
  });

  const primaryResult = { medication: orderInfo.value.medication, success: true, orderKey: orderInfo.value.orderKey };
  if (toSubmit.length === 0) {
    return [primaryResult, ...duplicatesOfPrimary];
  }
  const { results } = await orderInfoService.createSubmissionsBatch(sharedFields, toSubmit);
  return [primaryResult, ...duplicatesOfPrimary, ...results];
}

async function handleCreateSubmission() {
  scheduleAutoSave.cancel();
  formButtonClicked.value = true;
  errors.value = [];
  batchSubmissionResults.value = null;
  if (!validPatientInfo()) {
    expandedSections.value.push("patient-info");
    errors.value.push("Please fill in all required patient information fields.");
  }
  if (!validProviderInfo()) {
    expandedSections.value.push("provider-info");
    errors.value.push("Please fill in all required provider information fields.");
  }
  if (!validMedicationInfo() || !validAdditionalMedicationsInfo()) {
    expandedSections.value.push("medical-info");
    errors.value.push("Please fill in all required medication information fields.");
  }
  if (!validDiagnosisInfo()) {
    expandedSections.value.push("diagnosis-info");
    errors.value.push("Please fill in all required diagnosis information fields.");
  }
  if (!validSupportingDocs()) {
    expandedSections.value.push("supporting-docs");
    errors.value.push("Please add at least one supporting document.");
  }
  if (faxFormOptedIn.value && !orderInfo.value.faxSubmissionFormDetail) {
    if (!expandedSections.value.includes("submission-options")) expandedSections.value.push("submission-options");
    errors.value.push("Please select a fax form, or uncheck the fax submission option.");
  }
  await new Promise((resolve) => setTimeout(resolve, 300));
  const firstInvalidField = document.querySelector(".v-input--error, .form-validation-alert");
  if (errors.value.length === 0 && !firstInvalidField) {
    draftSavingInProgress.value = true;
    try {
      if (!orderInfo.value.orderKey) {
        const newOrder = await orderInfoService.createEmptyOrder(orderInfo.value);
        orderInfo.value.orderKey = newOrder.orderKey;
        orderInfo.value.assignees = newOrder.assignees;
        orderInfo.value.assignedGroups = newOrder.assignedGroups;
      }
      // The Create step rides on this request only: left in the store, a failed submit would have
      // the next auto-save PUT it and submit the order. The queued medications leave the primary
      // here too: the batch call below turns them into their own submissions, so the created order
      // must not keep listing them as still pending.
      await orderInfoService.saveOrder(orderInfo.value.orderKey, {
        ...orderInfo.value,
        formStep: "Create Submission",
        draftAdditionalMedications: [],
      });
    } catch (err) {
      errors.value = tryParseApiErrors(err);
      draftSavingInProgress.value = false;
      return;
    }
    // The primary is committed from here on — a batch failure (e.g. a 404 mid rolling-deploy,
    // or a network blip) must not be reported as if nothing was submitted, since something was.
    if (orderInfo.value.draftAdditionalMedications.length > 0) {
      try {
        batchSubmissionResults.value = await submitAdditionalMedications();
      } catch (err) {
        batchSubmissionResults.value = [
          { medication: orderInfo.value.medication, success: true, orderKey: orderInfo.value.orderKey },
          ...orderInfo.value.draftAdditionalMedications.map((medication) => ({
            medication: medication.medication,
            success: false,
            error: "Could not be submitted — please add it again from the Submissions list.",
          })),
        ];
      }
    }
    errors.value = [];
    expandedSections.value = [];
    scheduleAutoSave.cancel();
    showCreateSuccessDialog.value = true;
    draftSavingInProgress.value = false;
  } else if (firstInvalidField) {
    firstInvalidField.scrollIntoView({ behavior: "smooth", block: "center" });
  }
}

// The Worklist (with its filters) when the user came from it; else the Worklist's Prior Auth view, or the Prior Auth
// tab for a company without the Worklist.
const listTarget = computed(() => {
  const modules = requirementsStore.visibleModules;
  const fallback = worklistShownFor(requirementsStore.worklistEnabled, modules)
    ? priorAuthWorklistRoute(modules)
    : { name: "prior-auth" };
  return listReturnTarget(fallback);
});
const backLabel = computed(() =>
  router.resolve(listTarget.value).name === "worklist" ? "Back to Worklist" : "Back to Prior Auth"
);

function onSuccessReturnToAll() {
  showCreateSuccessDialog.value = false;
  batchSubmissionResults.value = null;
  clearOrderInfoStorage();
  router.push(listTarget.value);
  window.scrollTo(0, 0);
}

// Deliberately a full browser reload rather than an in-place reset or a router navigation.
//
// This page accumulates state in three places the parent can't reach in one sweep: the shared
// orderInfoStore, this component's own refs (autofill candidates, poll timer, errors), and local
// state inside children — the autofill card's extra-file uploads are private to it by design, so
// they never reach orderInfo.patientChart. Resetting that by hand means a list that has to grow
// every time page state is added, and a missed entry leaks the previous patient's data into the
// next submission. A reload starts from a guaranteed-empty page instead.
//
// A router navigation would not do it: this route is /create-submission/:id?, and Vue Router
// reuses the component across a param change — which syncDraftKeyToUrl() above relies on — so
// replacing the route drops the id without remounting anything.
//
// Safe to reload here: the submission is already committed server-side, the auth session lives in
// localStorage, and orderInfoStore is in-memory Pinia with no persistence, so nothing needs
// clearing first. Cost is a bundle re-fetch and a page flash on each "create another".
function onSuccessCreateAnother() {
  showCreateSuccessDialog.value = false;
  // resolve() rather than a literal path so the app's router base is honoured.
  window.location.assign(router.resolve({ name: "create-submission" }).href);
}
</script>

<style lang="scss" scoped>
.detail-page__back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 500;
  color: #5e7898;
  cursor: pointer;
  text-decoration: none;
  padding: 4px 0;
  margin-bottom: 8px;
  transition: color 150ms;

  &:hover {
    color: #052e5e;
  }
}
</style>
